﻿import AddIcon from "@mui/icons-material/Add";
import MessageIcon from "@mui/icons-material/Message";
import ShareIcon from "@mui/icons-material/Share";
import { Theme } from "@mui/material";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardActions from "@mui/material/CardActions";
import CardContent from "@mui/material/CardContent";
import CardMedia from "@mui/material/CardMedia";
import Chip from "@mui/material/Chip";
import Link from "@mui/material/Link";
import { styled } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import Guid from "_shared/guids/guid";
import { Currency, formatCurrency } from "_shared/money/currencies/currency";
import Money from "_shared/money/money";
import Name from "_shared/names/name";
import { MarketplaceResultType } from "config";
import MarketplaceChatCardContent from "marketplace/_views/marketplace-chat-card-content";
import IndividualBookmarkButton from "marketplace/bookmarks/_views/individual-bookmark-button";
import ViewCompanyProfile from "marketplace/companies/profiles/_views/view-company-profile";
import { individualsApi, useGetIndividualAvatarQuery } from "marketplace/individuals/_api/individual-api";
import { getIndividualAvatar } from "marketplace/individuals/_stores/individuals-redux-slice";
import Individual from "marketplace/individuals/individual";
import MarketplaceIndividualInfoSchema, {
  MarketplaceIndividualInfoJSON,
} from "marketplace/_schema/marketplace-individual-info-schema";
import ViewIndividualProfile from "marketplace/individuals/profiles/_views/individual-profile-container";
import ChatWindow from "messaging/forums/_views/components/forum-chat-window";
import Forum from "messaging/forums/forum";
import { enqueueSnackbar } from "notistack";
import React from "react";
import { useSelector } from "react-redux";
import { NavigateFunction } from "react-router";
import Session from "users/__session/session";
import { getIsNetworkedWith } from "users/networking/connections/_selectors/user-network-connection-selectors";
import NetworkButton from "users/networking/connections/_views/components/network-button";
import DirectProposalButton from "work/proposals/_views/_buttons/direct-proposal-button";
import GroupOffIcon from "@mui/icons-material/GroupOff";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import startDraftProposalWithVendorTeamLeader from "work/proposals/_views/drafts/start-draft-proposal-with-vendor-team-leader";

const ResultCard = styled(Card, {
  shouldForwardProp: (prop) => prop !== "compactLayout",
})<{ compactLayout?: boolean }>(({ theme, compactLayout }) => ({
  [theme.breakpoints.down("xl")]: {
    display: "block",
  },
  display: compactLayout ? "block" : "flex",
  flex: "1 0 auto",
  justifyContent: "space-between",
  marginBottom: compactLayout ? theme.spacing(1.5) : theme.spacing(2),
  marginTop: compactLayout ? theme.spacing(1.5) : theme.spacing(2),
  ...(compactLayout && {
    borderColor: theme.palette.divider,
    borderRadius: theme.spacing(0.75),
    boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
    display: "inline-block",
    overflow: "hidden",
    width: "100%",
  }),
  "&:first-of-type": {
    marginTop: 0,
  },
}));
const ResultHeader = styled("div", {
  shouldForwardProp: (prop) => prop !== "compactLayout",
})<{ compactLayout?: boolean }>(({ theme, compactLayout }) => ({
  alignItems: compactLayout ? "start" : "center",
  display: "grid",
  columnGap: compactLayout ? theme.spacing(1.25) : theme.spacing(2),
  gridTemplateColumns: compactLayout ? `${theme.spacing(8)} 1fr` : "auto 1fr",
}));
const ResultCardContent = styled(CardContent, {
  shouldForwardProp: (prop) => prop !== "compactLayout",
})<{ compactLayout?: boolean }>(({ theme, compactLayout }) => ({
  flexGrow: "inherit",
  padding: compactLayout ? theme.spacing(1.5) : theme.spacing(2),
  paddingLeft: compactLayout ? theme.spacing(1.5) : theme.spacing(3),
  "&:last-child": {
    paddingBottom: compactLayout ? theme.spacing(1.5) : undefined,
  },
}));
const ResultCardActions = styled(CardActions, {
  shouldForwardProp: (prop) => prop !== "compactLayout",
})<{ compactLayout?: boolean }>(({ theme, compactLayout }) => ({
  [theme.breakpoints.down("xl")]: {
    alignContent: "flex-end",
    alignItems: "unset",
    flexDirection: "row",
    justifyContent: "flex-end",
    marginBottom: theme.spacing(2),
  },
  [theme.breakpoints.down("lg")]: {
    alignContent: "flex-start",
    alignItems: "unset",
    flexDirection: "row",
    justifyContent: "flex-start",
    marginBottom: theme.spacing(2),
  },
  alignContent: "center",
  alignItems: compactLayout ? "unset" : "flex-end",
  flex: 0,
  flexDirection: compactLayout ? "row" : "column",
  flexWrap: "wrap",
  justifyContent: compactLayout ? "flex-start" : "space-evenly",
  marginBottom: compactLayout ? theme.spacing(2) : undefined,
  padding: compactLayout ? theme.spacing(0, 1.5, 1.5) : undefined,
  paddingRight: compactLayout ? theme.spacing(1.5) : theme.spacing(3),
}));
const ResultName = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "compactLayout",
})<{ compactLayout?: boolean }>(({ theme, compactLayout }) => ({
  cursor: "pointer",
  fontSize: compactLayout ? theme.typography.subtitle1.fontSize : undefined,
  fontWeight: compactLayout ? theme.typography.fontWeightBold : undefined,
  lineHeight: compactLayout ? 1.2 : undefined,
  textDecoration: "underline",
}));
const TypeChip = styled(Chip, {
  shouldForwardProp: (prop) => prop !== "compactLayout",
})<{ compactLayout?: boolean }>(({ theme, compactLayout }) => ({
  borderColor: "#33c",
  borderStyle: "dashed",
  color: "#33c",
  marginRight: theme.spacing(1),
  marginTop: compactLayout ? theme.spacing(0.75) : theme.spacing(0.5),
  opacity: 0.66,
  width: "fit-content",
}));
const StyledButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== "compactLayout",
})<{ compactLayout?: boolean }>(({ theme, compactLayout }) => ({
  [theme.breakpoints.down("sm")]: {
    width: "100%",
  },
  marginBottom: compactLayout ? theme.spacing(0.75) : theme.spacing(1),
  marginLeft: compactLayout ? 0 : theme.spacing(2),
  marginRight: compactLayout ? theme.spacing(0.75) : 0,
  minWidth: compactLayout ? 0 : theme.spacing(30),
}));
const StyledNetworkButton = styled(NetworkButton, {
  shouldForwardProp: (prop) => prop !== "compactLayout",
})<{ compactLayout?: boolean }>(({ theme, compactLayout }) => ({
  [theme.breakpoints.down("sm")]: {
    width: "100%",
  },
  marginBottom: compactLayout ? theme.spacing(0.75) : theme.spacing(1),
  marginLeft: compactLayout ? 0 : theme.spacing(2),
  marginRight: compactLayout ? theme.spacing(0.75) : 0,
  minWidth: compactLayout ? 0 : theme.spacing(30),
}));
const StyledBookmarkButton = styled(IndividualBookmarkButton, {
  shouldForwardProp: (prop) => prop !== "compactLayout",
})<{ compactLayout?: boolean }>(({ theme, compactLayout }) => ({
  [theme.breakpoints.down("sm")]: {
    width: "100%",
  },
  marginBottom: compactLayout ? theme.spacing(0.75) : theme.spacing(1),
  marginLeft: compactLayout ? 0 : theme.spacing(2),
  marginRight: compactLayout ? theme.spacing(0.75) : 0,
  minWidth: compactLayout ? 0 : theme.spacing(30),
}));
const Subtitle = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "compactLayout",
})<{ compactLayout?: boolean }>(({ theme, compactLayout }) => ({
  color: compactLayout ? theme.palette.text.secondary : undefined,
  fontSize: compactLayout ? theme.typography.body2.fontSize : undefined,
  fontWeight: compactLayout ? theme.typography.fontWeightBold : theme.typography.fontWeightMedium,
  letterSpacing: compactLayout ? 0.2 : undefined,
  lineHeight: compactLayout ? 1.3 : undefined,
  marginBottom: compactLayout ? theme.spacing(0.25) : undefined,
}));
const Details = styled("div", {
  shouldForwardProp: (prop) => prop !== "compactLayout",
})<{ compactLayout?: boolean }>(({ theme, compactLayout }) => ({
  [theme.breakpoints.up(1920)]: {
    columnCount: 2,
  },
  [theme.breakpoints.down(1920)]: {
    columnCount: 1,
  },
  [theme.breakpoints.down("xl")]: {
    columnCount: 2,
  },
  [theme.breakpoints.down("lg")]: {
    columnCount: 2,
  },
  [theme.breakpoints.down("md")]: {
    columnCount: 1,
  },
  columnCount: compactLayout ? "unset" : undefined,
  columnGap: theme.spacing(2),
  columnFill: "balance",
  display: compactLayout ? "flex" : undefined,
  flexDirection: compactLayout ? "column" : undefined,
  marginTop: compactLayout ? theme.spacing(1.5) : theme.spacing(2),
  ...(compactLayout && {
    paddingTop: 0,
    "& .MuiLink-root": {
      display: "block",
      fontSize: theme.typography.body2.fontSize,
      lineHeight: 1.35,
      marginBottom: theme.spacing(0.1),
      textUnderlineOffset: "2px",
    },
  }),
}));
const StyledLink = styled(Link)(({ theme }) => ({
  color: theme.palette.common.black,
  cursor: "pointer",
  textDecoration: "underline",
})) as typeof Link;
const SmallAvatar = styled(Avatar, {
  shouldForwardProp: (prop) => prop !== "compactLayout",
})<{ compactLayout?: boolean }>(({ theme, compactLayout }) => ({
  cursor: "pointer",
  height: compactLayout ? theme.spacing(8) : theme.spacing(10),
  width: compactLayout ? theme.spacing(8) : theme.spacing(10),
}));
const ResultAvatarCardMedia = styled(CardMedia)(() => ({
  cursor: "pointer",
  height: "auto",
  width: "15vw",
})) as typeof CardMedia;
const ResultAvatar = styled(Avatar)(() => ({
  cursor: "pointer",
  height: "auto",
  width: "15vw",
}));
const GridRow = styled("div", {
  shouldForwardProp: (prop) => prop !== "compactLayout",
})<{ compactLayout?: boolean }>(({ theme, compactLayout }) => ({
  display: "grid",
  borderBottom: compactLayout ? `1px solid ${theme.palette.divider}` : undefined,
  gap: 0,
  gridTemplateColumns: compactLayout ? "1fr" : "1fr 1fr",
  marginBottom: compactLayout ? 0 : theme.spacing(1),
  paddingBottom: compactLayout ? theme.spacing(1.5) : 0,
  paddingTop: compactLayout ? theme.spacing(1.5) : 0,
  ...(compactLayout && {
    marginLeft: 0,
    marginRight: 0,
  }),
  "&:last-child": {
    borderBottom: "none",
  },
}));
const SelectMemberButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== "compactLayout",
})<{ compactLayout?: boolean }>(({ theme, compactLayout }) => ({
  marginLeft: compactLayout ? 0 : theme.spacing(2),
  marginTop: compactLayout ? theme.spacing(1) : 0,
  width: compactLayout ? "100%" : undefined,
  "&.MuiButton-root": {
    width: compactLayout ? "100%" : undefined,
  },
}));
const NameContainer = styled("div", {
  shouldForwardProp: (prop) => prop !== "compactLayout",
})<{ compactLayout?: boolean }>(({ theme, compactLayout }) => ({
  display: "flex",
  alignItems: compactLayout ? "flex-start" : undefined,
  flexDirection: compactLayout ? "column" : "row",
  flexWrap: "wrap",
  gap: compactLayout ? theme.spacing(0.5) : 0,
}));
const HeaderText = styled("span", {
  shouldForwardProp: (prop) => prop !== "compactLayout",
})<{ compactLayout?: boolean }>(({ compactLayout }) => ({
  minWidth: 0,
  width: "100%",
  ...(compactLayout && {
    display: "flex",
    flexDirection: "column",
  }),
}));

export enum TeamMemberType {
  Leader = "leader",
  Member = "member",
}

type IndividualResultProps = {
  selectingAsLeader?: boolean;
  selectedTeamMemberUserIds?: Guid[];
  compactLayout?: boolean;
  result: MarketplaceIndividualInfoJSON;
  embedded: boolean;
  session: Session;
  navigate?: NavigateFunction;
  getText: (fieldNames: string[], base: unknown, noCommas?: boolean) => string;
  handleResultClicked: (
    event: React.MouseEvent,
    profileId: Guid,
    profileType: MarketplaceResultType,
    component: React.JSX.Element,
    fullWidth?: boolean,
  ) => void;
  onTeamMemberSelected?: (teamMember: Individual) => void;
};

function getBillingRateDisplayValue(billingRate: string): string {
  const sanitizedBillingRate = billingRate.replace(/[$,\s]/g, "");

  if (!/^-?(?:\d+|\d*\.\d+)$/.test(sanitizedBillingRate)) {
    return billingRate;
  }

  const parsedBillingRate = Number.parseFloat(sanitizedBillingRate);

  if (!Number.isFinite(parsedBillingRate)) {
    return billingRate;
  }

  return formatCurrency(new Money(parsedBillingRate, Currency.USD));
}

export default function IndividualResult(props: Readonly<IndividualResultProps>) {
  const {
    selectingAsLeader,
    selectedTeamMemberUserIds,
    compactLayout,
    result,
    embedded,
    session,
    navigate,
    getText,
    handleResultClicked,
    onTeamMemberSelected,
  } = props;

  const { firstName, lastName, id } = result;
  const individualId = React.useMemo(() => (id ? new Guid(id) : null), [id]);

  const [showChat, setShowChat] = React.useState<boolean>(false);
  const [forum, setForum] = React.useState<Forum>();
  const [isPublishing, setIsPublishing] = React.useState<boolean>(false);
  const [isPublished, setIsPublished] = React.useState<boolean>(false);
  const individual = MarketplaceIndividualInfoSchema.deserialize(result);
  // Only the individual may publish their own profile. The marketplace search also returns
  // unpublished members of a legal entity the caller can read, so "this result reached me"
  // does not imply "I may publish it" - acting on someone else's profile is a 401.
  const isOwnProfile = Boolean(result.userId && session.user?.id?.isEqualTo(new Guid(result.userId)));
  const isUnpublished = result.isVisible === false && !isPublished;
  const avatarId = result.avatarId;

  const dispatch = useAttorneyHubDispatch();
  const isXlDownDisplaySize = useMediaQuery((theme: Theme) => theme.breakpoints.down("xl"));
  const isXlUpDisplaySize = useMediaQuery((theme: Theme) => theme.breakpoints.up("xl"));

  const isNetworked = useSelector(getIsNetworkedWith(individual.userId));
  const avatar = useSelector(getIndividualAvatar(individualId, "medium"));
  useGetIndividualAvatarQuery(
    { individualId: individualId?.value ?? "", size: "medium" },
    { skip: !individualId || !!avatar || !avatarId },
  );

  async function contactIndividual() {
    try {
      if (!individualId) throw new Error("Individual ID is required to contact individual.");
      const forumResponse = await dispatch(
        individualsApi.endpoints.contactIndividual.initiate({ individualId: individualId.value }),
      ).unwrap();
      setForum(Forum.fromJSON(forumResponse));
      setShowChat(true);
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Couldn't find user", { variant: "error" });
    }
  }

  function getAddButton(result: MarketplaceIndividualInfoJSON) {
    let tooltipTitle = "";
    let buttonIcon = <AddIcon />;
    let buttonLabel = "Add To Team";

    const alreadyOnTeam = selectedTeamMemberUserIds?.some((id) => id.isEqualTo(new Guid(result.userId!)));
    const profileNotVisible = isUnpublished && isOwnProfile;
    const isDisabled = (selectingAsLeader && !result.hasSigningAuthority) || alreadyOnTeam || isPublishing;

    if (profileNotVisible) {
      tooltipTitle = "Publish your profile to the marketplace to add to vendor-team";
      buttonIcon = <AccountCircleIcon />;
      buttonLabel = "Publish Profile";
    }
    if (alreadyOnTeam) {
      tooltipTitle = "Already on vendor-team";
      buttonIcon = <GroupOffIcon />;
      buttonLabel = "On Team";
    }

    return (
      <Tooltip title={tooltipTitle}>
        <span style={{ display: compactLayout ? "block" : undefined, width: compactLayout ? "100%" : undefined }}>
          <SelectMemberButton
            compactLayout={compactLayout}
            variant="outlined"
            color="primary"
            aria-label={buttonLabel}
            startIcon={buttonIcon}
            disableElevation
            disabled={isDisabled}
            onClick={() => handleSelectMemberButtonClicked(result)}
          >
            {buttonLabel}
          </SelectMemberButton>
        </span>
      </Tooltip>
    );
  }

  function openIndividualProfile(event: React.MouseEvent, id: string | null | undefined) {
    if (!id) return;
    const profileId = new Guid(id);
    handleResultClicked(
      event,
      profileId,
      MarketplaceResultType.Individual,
      <ViewIndividualProfile individualId={profileId} />,
      true,
    );
  }

  function openCompanyProfile(event: React.MouseEvent, id: string | null | undefined) {
    if (!id) return;
    const profileId = new Guid(id);
    handleResultClicked(
      event,
      profileId,
      MarketplaceResultType.Company,
      <ViewCompanyProfile companyId={profileId} />,
      true,
    );
  }

  function handleSelectMemberButtonClicked(result: MarketplaceIndividualInfoJSON) {
    const individual = MarketplaceIndividualInfoSchema.deserialize(result);
    if (isUnpublished) {
      if (isOwnProfile) void publishProfile(individual);
      return;
    }
    onTeamMemberSelected?.(individual);
  }

  function canCreateProposalFromResult() {
    // Do not render button if result is self
    const resultUserId = result?.userId ? new Guid(result.userId) : undefined;
    if (!resultUserId || session.user?.id?.isEqualTo(resultUserId)) return false;

    const resultEntityId = result?.legalEntityId ? new Guid(result.legalEntityId) : undefined;
    if (!resultEntityId) return false;

    // Do not render button if user is same entity as target
    const userEntityId = session.user!.legalEntityId;
    return userEntityId?.isEqualTo(resultEntityId) === false;
  }

  function canRequestProposalFromResult() {
    return canCreateProposalFromResult() && Boolean(result.hasSigningAuthority);
  }

  function handleSendProposalToResult(targetUrl: URL): void {
    if (targetUrl.pathname === "/proposals/drafts/create" && result.userId && result.legalEntityId && navigate) {
      startDraftProposalWithVendorTeamLeader({
        dispatch,
        leader: {
          userId: new Guid(result.userId),
          legalEntityId: new Guid(result.legalEntityId),
        },
        navigate,
        session,
      });
      return;
    }

    navigate?.(targetUrl.pathname + targetUrl.search);
  }

  async function publishProfile(individual: Individual) {
    if (!individual.id) return;
    try {
      setIsPublishing(true);
      await dispatch(
        individualsApi.endpoints.updateIndividual.initiate({
          individualId: individual.id.value,
          isVisible: true,
        }),
      ).unwrap();
      setIsPublished(true);
      enqueueSnackbar("Profile published", { variant: "success" });
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Couldn't publish profile. Please try again", { variant: "error" });
    }
    setIsPublishing(false);
  }

  function getLocationText(): string {
    const location = result.location;
    if (!location) return "";
    if (typeof location === "string") return location;

    return [
      location.address1,
      location.address2,
      location.city,
      location.state,
      location.postalCode,
      location.country,
    ]
      .filter(Boolean)
      .join(", ");
  }

  const locationText = getLocationText();

  // Nothing here can be acted on: an unpublished individual cannot join a vendor-team, and only
  // they can publish themselves. Outside the picker the card still renders - reading the profile
  // is what the entity-wide member-information grant is for.
  if (embedded && isUnpublished && !isOwnProfile) return null;

  return (
    <React.Fragment>
      <ResultCard compactLayout={compactLayout} raised={false} variant="outlined">
        {!compactLayout && isXlUpDisplaySize && (
          <>
            <ResultAvatarCardMedia
              style={{ display: avatar ? undefined : "none" }}
              component="img"
              image={avatar}
              onClick={(event) => openIndividualProfile(event, result?.id)}
            />
            <ResultAvatar
              style={{ display: avatar ? "none" : undefined }}
              variant="square"
              onClick={(event) => openIndividualProfile(event, result?.id)}
            />
          </>
        )}
        <ResultCardContent compactLayout={compactLayout}>
          <ResultHeader compactLayout={compactLayout}>
            {(compactLayout || isXlDownDisplaySize) && (
              <span>
                <SmallAvatar
                  compactLayout={compactLayout}
                  style={{ display: avatar ? undefined : "none" }}
                  variant="circular"
                  src={avatar}
                  onClick={(event) => openIndividualProfile(event, result?.id)}
                />
                <SmallAvatar
                  compactLayout={compactLayout}
                  style={{ display: avatar ? "none" : undefined }}
                  variant="circular"
                  onClick={(event) => openIndividualProfile(event, result?.id)}
                />
              </span>
            )}
            <HeaderText compactLayout={compactLayout}>
              <NameContainer compactLayout={compactLayout}>
                <ResultName
                  compactLayout={compactLayout}
                  variant="h5"
                  onClick={(event) => openIndividualProfile(event, result?.id)}
                >
                  {getText(["firstName", "lastName"], result, true)}
                </ResultName>
                {!compactLayout && embedded && getAddButton(result)}
              </NameContainer>
              <TypeChip compactLayout={compactLayout} label="Individual" variant="outlined" size="small" />
            </HeaderText>
          </ResultHeader>
          {compactLayout && embedded && getAddButton(result)}
          <Details compactLayout={compactLayout}>
            {result?.companyName && (
              <GridRow compactLayout={compactLayout}>
                <Subtitle compactLayout={compactLayout} variant="body1">Company</Subtitle>
                <StyledLink
                  component={Typography}
                  onClick={(event) => openCompanyProfile(event, result?.companyId)}
                >
                  {getText(["companyName"], result)}
                </StyledLink>
              </GridRow>
            )}
            {(result?.technicalBackgrounds?.length ?? 0) > 0 && (
              <GridRow compactLayout={compactLayout}>
                <Subtitle compactLayout={compactLayout} variant="body1">Technical Background</Subtitle>
                <Typography variant="body2">{result?.technicalBackgrounds?.join(", ")}</Typography>
              </GridRow>
            )}
            {(result?.practiceAreas?.length ?? 0) > 0 && (
              <GridRow compactLayout={compactLayout}>
                <Subtitle compactLayout={compactLayout} variant="body1">Practice Area</Subtitle>
                <Typography variant="body2">{result?.practiceAreas?.join(", ")}</Typography>
              </GridRow>
            )}
            {result?.barAdmissionYear && (
              <GridRow compactLayout={compactLayout}>
                <Subtitle compactLayout={compactLayout} variant="body1">Bar Admission Year</Subtitle>
                <Typography variant="body2">{getText(["barAdmissionYear"], result)}</Typography>
              </GridRow>
            )}
            {locationText && (
              <GridRow compactLayout={compactLayout}>
                <Subtitle compactLayout={compactLayout} variant="body1">Location</Subtitle>
                <Typography variant="body2">{locationText}</Typography>
              </GridRow>
            )}
            {result?.billingRate && result.billingRate !== "" && (
              <GridRow compactLayout={compactLayout}>
                <Subtitle compactLayout={compactLayout} variant="body1">Billing Rate</Subtitle>
                <Box sx={{ display: "flex" }}>
                  <Typography>{getBillingRateDisplayValue(result.billingRate)}</Typography>
                  {result.billingUnit && result.billingUnit !== "" && (
                    <>
                      <Typography variant="body2">&nbsp;/&nbsp;</Typography>
                      <Typography variant="body2">{getText(["billingUnit"], result)}</Typography>
                    </>
                  )}
                </Box>
              </GridRow>
            )}
            {result?.regionsRegistered && result?.regionsRegistered?.length > 0 && (
              <GridRow compactLayout={compactLayout}>
                <Subtitle compactLayout={compactLayout} variant="body1">Regions Registered In</Subtitle>
                <Typography variant="body2">{getText(["regionsRegistered"], result)}</Typography>
              </GridRow>
            )}
          </Details>
        </ResultCardContent>
        {!embedded && (
          <ResultCardActions compactLayout={compactLayout} disableSpacing>
            <StyledNetworkButton compactLayout={compactLayout} userId={individual.userId} />
            <StyledBookmarkButton compactLayout={compactLayout} individualId={individual.id} />
            <Tooltip title={isNetworked ? "" : "You must be in a individual's network before contacting them"}>
              <div style={{ display: "contents", width: "100%" }}>
                <StyledButton
                  compactLayout={compactLayout}
                  startIcon={<MessageIcon />}
                  variant="outlined"
                  disabled={!isNetworked}
                  onClick={contactIndividual}
                >
                  Send Message
                </StyledButton>
              </div>
            </Tooltip>
            <StyledButton
              compactLayout={compactLayout}
              aria-label="share"
              variant="outlined"
              startIcon={<ShareIcon />}
              onClick={() => {
                navigator.clipboard
                  .writeText(`${globalThis.location.origin}/marketplace/individual/${result?.id}`)
                  .then(() => {
                    enqueueSnackbar("Link copied to clipboard", { variant: "info" });
                  })
                  .catch(() => {
                    enqueueSnackbar("Couldn't copy link to clipboard", { variant: "error" });
                  });
              }}
            >
              Share
            </StyledButton>
            {canCreateProposalFromResult() && (
              <>
                <DirectProposalButton
                  targetUserId={new Guid(result.userId!)}
                  targetEntityId={new Guid(result.legalEntityId!)}
                  targetName={Name.create({ params: { firstName, lastName } })}
                  targetHasSigningAuthority={result.hasSigningAuthority ?? undefined}
                  onClick={handleSendProposalToResult}
                  useStyledButton={true}
                  action={"create"}
                />
                {canRequestProposalFromResult() && (
                  <DirectProposalButton
                    targetUserId={new Guid(result.userId!)}
                    targetEntityId={new Guid(result.legalEntityId!)}
                    targetName={Name.create({ params: { firstName, lastName } })}
                    targetHasSigningAuthority={result.hasSigningAuthority ?? undefined}
                    onClick={handleSendProposalToResult}
                    useStyledButton={true}
                    action={"request"}
                  />
                )}
              </>
            )}
          </ResultCardActions>
        )}
      </ResultCard>
      {showChat && (
        <MarketplaceChatCardContent>
          <ChatWindow fillAvailableSpace forums={forum ? [forum] : []} onClose={() => setShowChat(false)} />
        </MarketplaceChatCardContent>
      )}
    </React.Fragment>
  );
}
